"use client"

import { useState, useEffect, useRef } from "react"
import { ArrowRight, ArrowLeft, Home, RotateCcw } from "lucide-react"
import TeachingAvatar from "./TeachingAvatar"
import Confetti from "./Confetti"
import NavigationMenu from "./NavigationMenu"
import { useLanguage } from "./LanguageContext"
import { loadGameConfiguration } from "./ContentLoader"
import "./css/theme-variables.css"
import "./css/base-styles.css"
import "./css/navigation.css"
import "./components/App.css" // Import the App-specific CSS
import { ThemeProvider } from "./ThemeManager" // Add this import

// Translations for App component
const translations = {
  en: {
    loading: "Loading lesson...",
    startAgain: "Start Again",
    startAdventure: "Start Activity",
    continueToNext: (section) => `Continue to ${section?.title?.split(" ")[0]}`,
    completeActivity: "Complete Activity",
    continue: "Continue",
    checkAnswer: "Check Answer",
    back: "Back",
    errorLoading: "Error loading content",
    tryAgain: "Try Again",
    selectNewGame: "Select a new game", // Add this line
  },
  nl: {
    loading: "Les laden...",
    startAgain: "Opnieuw beginnen",
    startAdventure: "Start Activiteit",
    continueToNext: (section) => `Doorgaan naar ${section?.title?.split(" ")[0]}`,
    completeActivity: "Activiteit afronden",
    continue: "Doorgaan",
    checkAnswer: "Antwoord controleren",
    back: "Terug",
    errorLoading: "Fout bij het laden van inhoud",
    tryAgain: "Probeer opnieuw",
    selectNewGame: "Selecteer een nieuw spel", // Add this line
  },
}

const createCelebrationStep = (section) => {
  if (!section || !section.achievements || section.achievements.length === 0) {
    return null
  }

  return {
    type: "celebration",
    content: {
      title: `Goed Gedaan! 🎉`,
      description: `Je hebt alle stappen van "${section.title}" voltooid!`,
      achievements: section.achievements,
    },
  }
}

const DesignAdventureApp = ({ gameId, onReturnHome, beta = false }) => {
  // Get current language context
  const { language } = useLanguage()
  const effectiveLanguage = language.code || "en"
  const t = translations[language.code] || translations.en
  const [showingCelebration, setShowingCelebration] = useState(false)

  const [sections, setSections] = useState([])
  const [gameConfig, setGameConfig] = useState({
    title: "Loading...",
    emoji: "🔄",
  })
  const [activeSection, setActiveSection] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [isChecking, setIsChecking] = useState(false)
  const [score, setScore] = useState(0)
  const [streak, setStreak] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [navigationHistory, setNavigationHistory] = useState([])
  const audioControlsRef = useRef(null)
  const currentLessonId = `${gameId}-${activeSection}-${currentStep}`

  const currentSection = sections[activeSection]
  const currentContent =
    showingCelebration && currentSection?.celebrationStep
      ? currentSection.celebrationStep
      : currentSection?.steps[currentStep]

  const isLastSection = activeSection === sections.length - 1
  const isLastStep = currentStep === currentSection?.steps.length - 1
  const isIntroSection = activeSection === 0 && currentStep === 0
  const isSuccessSection = currentSection?.key === "success" || false
  const isCelebrationSection =
    showingCelebration ||
    currentContent?.type === "celebration" ||
    (currentSection?.key?.includes("celebration") && !isSuccessSection) ||
    (isLastStep && showingCelebration && !isSuccessSection) ||
    false

  // Load content based on gameId and language
  useEffect(() => {
    // Set loading state
    setIsLoading(true)
    setError(null)

    // Load game configuration and sections using the ContentLoader service
    loadGameConfiguration(gameId, language.code, beta)
      .then(({ gameConfig, sections }) => {
        // Set game configuration
        setGameConfig(gameConfig)

        // Check if the game has an external URL and redirect if needed
        if (gameConfig.externalUrl) {
          console.log(`Redirecting to external URL: ${gameConfig.externalUrl}`)
          window.location.href = gameConfig.externalUrl
          return // Stop further processing
        }

        // If we're in beta mode, add a beta indicator to the game config
        if (beta) {
          gameConfig.isBeta = true
        }

        // Process sections - add SRT-only mode support
        const processedSections = sections.map((section) => {
          // Process steps within each section
          if (section.steps && Array.isArray(section.steps)) {
            const processedSteps = section.steps.map((step) => {
              // Mark steps for different playback modes
              if (step.content) {
                // Preserve fullHeightVideo flag if present
                const fullHeightVideo = step.content.fullHeightVideo || false

                if (step.content.srtSrc && !step.content.audioSrc) {
                  // SRT-only mode
                  return {
                    ...step,
                    content: {
                      ...step.content,
                      isSrtOnlyMode: true,
                      fullHeightVideo: fullHeightVideo,
                    },
                  }
                } else if (!step.content.srtSrc && !step.content.audioSrc) {
                  // Text-only mode
                  return {
                    ...step,
                    content: {
                      ...step.content,
                      isTextOnlyMode: true,
                      fullHeightVideo: fullHeightVideo,
                    },
                  }
                }

                // Add this new code to process downloadable files
                if (step.content) {
                  // Process downloadable file at content level
                  if (step.content && step.content.downloadableFile) {
                    // Make sure the filePath is processed correctly
                    if (
                      step.content.downloadableFile.filePath &&
                      !step.content.downloadableFile.filePath.startsWith("http")
                    ) {
                      // Check if it's already a full path or a relative path
                      if (!step.content.downloadableFile.filePath.startsWith("/")) {
                        step.content.downloadableFile.filePath = `/content/${effectiveLanguage}/${gameId}/${step.content.downloadableFile.filePath}`
                      }
                    }

                    // Do the same for previewUrl
                    if (
                      step.content.downloadableFile.previewUrl &&
                      !step.content.downloadableFile.previewUrl.startsWith("http")
                    ) {
                      if (!step.content.downloadableFile.previewUrl.startsWith("/")) {
                        step.content.downloadableFile.previewUrl = `/content/${effectiveLanguage}/${gameId}/${step.content.downloadableFile.previewUrl}`
                      }
                    }

                    // Log for debugging
                    console.log("Processed downloadable file paths:", {
                      filePath: step.content.downloadableFile.filePath,
                      previewUrl: step.content.downloadableFile.previewUrl,
                    })
                  }

                  // Process downloadable files in columns
                  if (step.content.columns && Array.isArray(step.content.columns)) {
                    step.content.columns = step.content.columns.map((column) => {
                      if (column.downloadableFile) {
                        if (column.downloadableFile.filePath && !column.downloadableFile.filePath.startsWith("http")) {
                          column.downloadableFile.filePath
                        }
                        if (column.downloadableFile.fileName && !column.downloadableFile.fileName.startsWith("http")) {
                          column.downloadableFile.fileName
                        }
                      }
                      return column
                    })
                  }

                  // Process downloadable files in parts
                  if (step.content.parts && Array.isArray(step.content.parts)) {
                    step.content.parts = step.content.parts.map((part) => {
                      if (part.downloadableFile) {
                        if (part.downloadableFile.filePath && !part.downloadableFile.filePath.startsWith("http")) {
                          part.downloadableFile.filePath
                        }
                        if (part.downloadableFile.fileName && !part.downloadableFile.fileName.startsWith("http")) {
                          part.downloadableFile.fileName
                        }
                      }

                      // Handle columns in parts too
                      if (part.columns && Array.isArray(part.columns)) {
                        part.columns = part.columns.map((column) => {
                          if (column.downloadableFile) {
                            if (
                              column.downloadableFile.filePath &&
                              !column.downloadableFile.filePath.startsWith("http")
                            ) {
                              column.downloadableFile.filePath
                            }
                            if (
                              column.downloadableFile.fileName &&
                              !column.downloadableFile.fileName.startsWith("http")
                            ) {
                              column.downloadableFile.fileName
                            }
                          }
                          return column
                        })
                      }

                      return part
                    })
                  }
                }
              }

              return step
            })

            return {
              ...section,
              steps: processedSteps,
            }
          }
          return section
        })

        setSections(processedSections)

        // Reset app state
        setActiveSection(0)
        setCurrentStep(0)
        setSelectedAnswer(null)
        setIsChecking(false)
        setScore(0)
        setStreak(0)

        // Initialize navigation history
        setNavigationHistory([{ section: 0, step: 0 }])

        // End loading state
        setIsLoading(false)
      })
      .catch((error) => {
        console.error(`Error loading game ${gameId}:`, error)
        setError(error.message || "Failed to load game content")
        setIsLoading(false)
      })
  }, [gameId, language, beta]) // Add beta as a dependency

  useEffect(() => {
    // Scroll to top immediately when section or step changes
    window.scrollTo({
      top: 0,
      behavior: "auto", // Use 'auto' instead of 'smooth' for instant jumps
    })
  }, [activeSection, currentStep])

  // Persist controls visibility state to localStorage
  useEffect(() => {
    // Convert boolean to string for localStorage
    if (typeof controlsVisible === "boolean") {
      localStorage.setItem("audioControlsVisible", controlsVisible.toString())
    }
  }, [controlsVisible])

  // Smooth scroll to top when navigating
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [activeSection, currentStep])

  // Add keyboard navigation for arrow keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle arrow keys if not in an input field
      if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") {
        return
      }

      // Right arrow to navigate to next step
      if (event.key === "ArrowRight") {
        // Only navigate if we're not checking a quiz answer
        if (!(currentContent?.type === "quiz" && !isChecking && selectedAnswer === null)) {
          handleNext()
        }
      }

      // Left arrow to navigate to previous step
      if (event.key === "ArrowLeft") {
        // Don't go back if we're at the first step of the first section
        if (!(currentStep === 0 && activeSection === 0)) {
          handleBack()
        }
      }

      // Shift + Right arrow to navigate to next section
      if (event.key === "ArrowRight" && event.shiftKey) {
        if (!isLastSection) {
          // Save current controls state
          const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

          // Stop any playing audio
          if (audioControlsRef.current && audioControlsRef.current.pause) {
            audioControlsRef.current.pause()
          }

          // Move to next section
          setActiveSection(activeSection + 1)
          setCurrentStep(0)
          setSelectedAnswer(null)
          setIsChecking(false)

          // Auto-play audio for the next section after a short delay
          playAudioAfterNavigation()

          // Make sure controls state is preserved
          updateControlsVisibility(currentControlsVisible)
        }
      }

      // Shift + Left arrow to navigate to previous section
      if (event.key === "ArrowLeft" && event.shiftKey) {
        if (activeSection > 0) {
          // Save current controls state
          const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

          // Stop any playing audio
          if (audioControlsRef.current && audioControlsRef.current.pause) {
            audioControlsRef.current.pause()
          }

          // Move to previous section
          setActiveSection(activeSection - 1)
          setCurrentStep(0)
          setSelectedAnswer(null)
          setIsChecking(false)

          // Auto-play audio for the previous section after a short delay
          playAudioAfterNavigation()

          // Make sure controls state is preserved
          updateControlsVisibility(currentControlsVisible)
        }
      }
    }

    // Add event listener
    window.addEventListener("keydown", handleKeyDown)

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [activeSection, currentStep, currentContent, isChecking, selectedAnswer, isLastSection, controlsVisible])

  // Helper function to update controls visibility
  const updateControlsVisibility = (isVisible) => {
    setControlsVisible(isVisible)
    if (typeof isVisible === "boolean") {
      localStorage.setItem("audioControlsVisible", isVisible.toString())
    }

    // Also update current controls if ref is available
    if (audioControlsRef.current && audioControlsRef.current.setControlsVisible) {
      audioControlsRef.current.setControlsVisible(isVisible)
    }
  }

  // Helper function to handle audio playback after navigation
  const playAudioAfterNavigation = (delay = 500) => {
    setTimeout(() => {
      if (audioControlsRef.current && audioControlsRef.current.play) {
        audioControlsRef.current.play().catch((err) => {
          console.log("Auto-play failed. This might be due to browser autoplay policies:", err)
        })
      }
    }, delay)
  }

  // Stop audio and preserve controls state when changing sections or steps
  useEffect(() => {
    // Save controls visibility state
    if (audioControlsRef.current && audioControlsRef.current.getControlsVisible) {
      setControlsVisible(audioControlsRef.current.getControlsVisible())
    }

    // Stop any playing audio when changing sections or steps
    if (audioControlsRef.current && audioControlsRef.current.pause) {
      audioControlsRef.current.pause()
    }
  }, [activeSection, currentStep])

  // If error occurred during loading
  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">{t.errorLoading}</h2>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <button onClick={onReturnHome} className="error-back-button">
              <Home className="error-back-icon" />
              {t.back}
            </button>
            <button onClick={() => window.location.reload()} className="error-retry-button">
              {t.tryAgain}
            </button>
          </div>
        </div>
      </div>
    )
  }
  // If still loading or no sections, show loading state
  if (isLoading || sections.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-icon">🔄</div>
          <h2 className="loading-title">{t.loading}</h2>
        </div>
      </div>
    )
  }

  // Add console.log for debugging
  console.log("Celebration detection:", {
    showingCelebration,
    isTypeMatch: currentContent?.type === "celebration",
    isKeyMatch: currentSection?.key?.includes("celebration"),
    isLastStepAndCelebrating: isLastStep && showingCelebration,
    result: isCelebrationSection,
    achievements: currentSection?.achievements,
  })

  console.log("Section status check:", {
    isLastSection,
    isLastStep,
    isCelebration: isCelebrationSection,
    showingCelebration,
    sectionHasAchievements: !!currentSection?.achievements,
    currentSectionKey: currentSection?.key,
    currentStepType: currentContent?.type,
  })
  const isHomeActive = activeSection === 0

  const handleAnswer = (index) => {
    // Allow changing answers even when checking
    setSelectedAnswer(index)
  }

  // Handle back button navigation
  const handleBack = () => {
    // Save current controls state
    const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

    // Stop any playing audio
    if (audioControlsRef.current && audioControlsRef.current.pause) {
      audioControlsRef.current.pause()
    }

    // First priority: go back one step within the current section if possible
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      setSelectedAnswer(null)
      setIsChecking(false)

      // Auto-play audio for the previous step after a short delay
      playAudioAfterNavigation()

      // If using navigation history, update it to reflect the back navigation
      if (navigationHistory.length > 1) {
        const newHistory = [...navigationHistory]
        newHistory.pop()
        setNavigationHistory(newHistory)
      }
    }
    // Second priority: if we're at the first step of a section (not the intro), go to the previous section's last step
    else if (activeSection > 0) {
      const prevSection = sections[activeSection - 1]
      const prevStepIndex = prevSection.steps.length - 1
      setActiveSection(activeSection - 1)
      setCurrentStep(prevStepIndex)
      setSelectedAnswer(null)
      setIsChecking(false)

      // Auto-play audio for the previous section after a short delay
      playAudioAfterNavigation()

      // If using navigation history, update it to reflect the back navigation
      if (navigationHistory.length > 1) {
        const newHistory = [...navigationHistory]
        newHistory.pop()
        setNavigationHistory(newHistory)
      }
    }

    // Make sure controls state is preserved
    updateControlsVisibility(currentControlsVisible)
  }

  const handleNextSection = () => {
    // Save current controls state
    const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

    // Stop any playing audio before section change
    if (audioControlsRef.current && audioControlsRef.current.pause) {
      audioControlsRef.current.pause()
    }

    // This console log is important for debugging
    console.log("handleNextSection called", {
      isSuccessSection,
      showingCelebration,
      activeSection,
      currentStep,
    })

    if (isSuccessSection) {
      // Reset to intro when "Start Again" is clicked
      setActiveSection(0)
      setCurrentStep(0)
      setSelectedAnswer(null)
      setIsChecking(false)
      setScore(0)
      setStreak(0)
      setShowingCelebration(false) // Important: Reset this flag
      // Reset navigation history
      setNavigationHistory([{ section: 0, step: 0 }])

      // Auto-play audio for the intro after a short delay
      playAudioAfterNavigation()
    } else {
      // Important: Always reset showingCelebration when moving to next section
      setShowingCelebration(false)

      // Move to next section
      setActiveSection(activeSection + 1)
      setCurrentStep(0) // Always start at the first step of the next section
      setSelectedAnswer(null)
      setIsChecking(false)

      // Auto-play audio for the next section after a short delay
      playAudioAfterNavigation()
    }

    // Make sure controls state is preserved
    updateControlsVisibility(currentControlsVisible)
  }

  // Update handleNext to hide feedback when navigating
  const handleNext = () => {
    // Save current controls state
    const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

    if (currentContent?.type === "quiz") {
      if (!isChecking && selectedAnswer !== null) {
        // First click on Check Answer: Just set checking state and update score
        setIsChecking(true)

        if (currentContent.content.options[selectedAnswer].correct) {
          setScore(score + 1)
          setStreak(streak + 1)
        } else {
          setStreak(0)
        }
        // Return early - this is crucial for seeing the feedback
        return
      } else if (isChecking) {
        // Second click on Continue: Move to next step/section
        if (audioControlsRef.current && audioControlsRef.current.pause) {
          audioControlsRef.current.pause()
        }

        // Modified condition - simplify and make more robust
        if (isLastStep) {
          // Check if we should skip the celebration based on the skipAchievementCelebration flag
          const shouldSkipCelebration = currentSection?.skipAchievementCelebration === true

          // Log for debugging
          if (shouldSkipCelebration && currentSection?.achievements && currentSection.achievements.length > 0) {
            console.log("SKIPPING CELEBRATION for section due to skipAchievementCelebration flag:", currentSection.key)
          }

          // If we're at the end of the section and the section has achievements,
          // show celebration before proceeding to next section (unless skipAchievementCelebration is true)
          if (
            !shouldSkipCelebration &&
            currentSection?.achievements &&
            currentSection.achievements.length > 0 &&
            !showingCelebration
          ) {
            console.log("SHOWING CELEBRATION for section:", currentSection.key)

            // Add a small delay before showing celebration
            setTimeout(() => {
              setShowingCelebration(true)
            }, 50)

            return // Important: Return here to stop execution and show celebration
          } else if (showingCelebration) {
            // If we're already showing a celebration, proceed to the next section
            setShowingCelebration(false)
            handleNextSection()
          } else {
            // Normal last step behavior with no achievements or when celebration is skipped - move to next section
            handleNextSection()
          }
        } else {
          // Add current position to history before moving to next step
          setNavigationHistory([
            ...navigationHistory,
            {
              section: activeSection,
              step: currentStep,
            },
          ])

          setCurrentStep(currentStep + 1)
          setSelectedAnswer(null)
          setIsChecking(false)

          // Auto-play audio for the next step
          playAudioAfterNavigation()
        }
      }
    } else {
      // For non-quiz content
      if (audioControlsRef.current && audioControlsRef.current.pause) {
        audioControlsRef.current.pause()
      }

      // Modified condition - simplify and make more robust
      if (isLastStep) {
        // Check if we should skip the celebration based on the skipAchievementCelebration flag
        const shouldSkipCelebration = currentSection?.skipAchievementCelebration === true

        // Log for debugging
        if (shouldSkipCelebration && currentSection?.achievements && currentSection.achievements.length > 0) {
          console.log("SKIPPING CELEBRATION for section due to skipAchievementCelebration flag:", currentSection.key)
        }

        // If we're at the end of the section and the section has achievements,
        // show celebration before proceeding to next section (unless skipAchievementCelebration is true)
        if (
          !shouldSkipCelebration &&
          currentSection?.achievements &&
          currentSection.achievements.length > 0 &&
          !showingCelebration
        ) {
          console.log("SHOWING CELEBRATION for section:", currentSection.key)

          // Add a small delay before showing celebration
          setTimeout(() => {
            setShowingCelebration(true)
          }, 50)

          return // Important: Return here to stop execution and show celebration
        } else if (showingCelebration) {
          // If we're already showing a celebration, proceed to the next section
          setShowingCelebration(false)
          handleNextSection()
        } else {
          // Normal last step behavior with no achievements or when celebration is skipped - move to next section
          handleNextSection()
        }
      } else {
        // Not at the last step, just proceed to next step
        // Add current position to history before moving to next step
        setNavigationHistory([
          ...navigationHistory,
          {
            section: activeSection,
            step: currentStep,
          },
        ])

        setCurrentStep(currentStep + 1)

        // Auto-play audio for the next step
        playAudioAfterNavigation()
      }
    }

    // Make sure controls state is preserved
    updateControlsVisibility(currentControlsVisible)
  }

  const handleCelebrationContinue = () => {
    // When continuing from a celebration screen, make sure to:
    // 1. Reset the celebration state
    // 2. Move to the next section

    console.log("Continuing from celebration for section:", currentSection?.key)
    setShowingCelebration(false)

    // Move to next section
    handleNextSection()
  }
  const getButtonText = () => {
    if (isSuccessSection) return t.startAgain
    if (isIntroSection) return t.startAdventure
    if (isCelebrationSection) {
      const nextSection = sections[activeSection + 1]
      return t.continueToNext(nextSection)
    }
    if (currentContent?.type === "quiz") {
      return isChecking ? t.continue : t.checkAnswer
    }
    return isLastStep ? t.completeActivity : t.continue
  }
  const getButtonIcon = () => {
    if (isSuccessSection && getButtonText() === "Opnieuw beginnen") {
      return <RotateCcw className="w-5 h-5" />
    }
    return <ArrowRight className="w-5 h-5" />
  }

  const avatarProps = {
    lessonId: currentLessonId,
    mainContent: currentContent?.content?.title,
    visualContent:
      isCelebrationSection || isSuccessSection
        ? {
            // Make sure title is passed properly
            title: currentContent?.content?.title || currentSection?.title,
            // This is the critical part - pass achievements from the right source
            achievements: currentContent?.content?.achievements || currentSection?.achievements || [],
            isCelebration: true,
            // Add reference to any celebration content in the section
            celebrationContent: currentSection?.celebrationContent,
          }
        : currentContent?.content,
    // CRITICAL: Also check for celebrationContent for speech text
    spokenContent:
      // For celebrations, look for specific celebration content
      isSuccessSection && currentContent?.content?.spokenContent
        ? currentContent.content.spokenContent
        : // For regular celebrations, look for celebrationContent
          isCelebrationSection && currentSection?.celebrationContent?.spokenContent
          ? currentSection.celebrationContent.spokenContent
          : // Then fall back to normal spoken content handling for other screens
            isChecking && selectedAnswer !== null && currentContent?.type === "quiz"
            ? currentContent.content.options[selectedAnswer]?.feedback
            : currentContent?.content.spokenContent,

    hideAudioControls:
      // Check in multiple places with a priority order:
      currentContent?.content?.hideAudioControls === true ||
      (isCelebrationSection && currentSection?.celebrationContent?.hideAudioControls === true) ||
      // Allow the section itself to define this property globally
      currentSection?.hideAudioControls === true ||
      // Check if this is a specific step type that should hide controls by default
      currentContent?.type === "noAudioControls",

    // Rest of the props remain the same
    audioSrc: currentContent?.content.audioSrc,
    srtSrc: currentContent?.content.srtSrc,
    quizOptions: currentContent?.type === "quiz" ? currentContent.content.options : undefined,
    onSelectAnswer: handleAnswer,
    selectedAnswer,
    isCorrect:
      isChecking && selectedAnswer !== null ? currentContent?.content.options?.[selectedAnswer]?.correct : null,
    isChecking,
    isCelebration: isCelebrationSection,
    isSuccess: isSuccessSection,
    audioControlsRef: audioControlsRef,
    initialControlsVisible: controlsVisible,
    state:
      currentContent?.type === "quiz" && isChecking && selectedAnswer !== null
        ? currentContent?.content.options?.[selectedAnswer]?.correct
          ? "happy"
          : "thinking"
        : currentSection?.maxStates?.intro?.state || "wave",
  }

  return (
    <ThemeProvider>
      <div className="app-container">
        {/* Title Bar */}

        {/* Navigation Bar */}
        <NavigationMenu
          sections={sections}
          activeSection={activeSection}
          setActiveSection={(newSection) => {
            // Save current controls state
            const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

            // Stop any playing audio
            if (audioControlsRef.current && audioControlsRef.current.pause) {
              audioControlsRef.current.pause()
            }

            // Add current position to history before moving to new section
            setNavigationHistory([
              ...navigationHistory,
              {
                section: activeSection,
                step: currentStep,
              },
            ])

            setActiveSection(newSection)
            setCurrentStep(0)
            setSelectedAnswer(null)
            setIsChecking(false)

            // Auto-play audio for the new section after a short delay
            playAudioAfterNavigation()

            // Make sure controls state is preserved
            updateControlsVisibility(currentControlsVisible)
          }}
          setCurrentStep={(newStep) => {
            // Save current controls state
            const currentControlsVisible = audioControlsRef.current?.getControlsVisible?.() || controlsVisible

            // Stop any playing audio
            if (audioControlsRef.current && audioControlsRef.current.pause) {
              audioControlsRef.current.pause()
            }

            // Add current position to history before moving to new step
            setNavigationHistory([
              ...navigationHistory,
              {
                section: activeSection,
                step: currentStep,
              },
            ])

            setCurrentStep(newStep)
            setSelectedAnswer(null)
            setIsChecking(false)

            // Auto-play audio for the new step after a short delay
            playAudioAfterNavigation()

            // Make sure controls state is preserved
            updateControlsVisibility(currentControlsVisible)
          }}
          score={score}
          streak={streak}
          isHomeActive={isHomeActive}
          onReturnHome={onReturnHome}
          gameConfig={gameConfig}
          setSelectedAnswer={setSelectedAnswer}
          setIsChecking={setIsChecking}
        />

        {/* Main Content */}
        <main className="main-content">
          {/* Render confetti with key to force re-render on section change */}
          {(isCelebrationSection || isSuccessSection) && (
            <Confetti key={`confetti-${activeSection}-${currentStep}`} count={75} />
          )}
          <TeachingAvatar {...avatarProps} />
        </main>

        <div className="footer-navigation">
          <div className="footer-content">
            {isIntroSection || isCelebrationSection || isSuccessSection ? (
              <div className="centered-button-container">
                {/* For success screen, show buttons side by side */}
                {isSuccessSection ? (
                  <div className="centered-button-row">
                    <button onClick={handleNextSection} className="main-action-button">
                      {getButtonText()}
                      {getButtonIcon()}
                    </button>

                    <button onClick={onReturnHome} className="secondary-action-button">
                      {t.selectNewGame}
                      <Home className="w-5 h-5" />
                    </button>
                  </div>
                ) : isCelebrationSection ? (
                  // Special button for celebration screens
                  <button onClick={handleCelebrationContinue} className="main-action-button">
                    {getButtonText()}
                    {getButtonIcon()}
                  </button>
                ) : (
                  // Button for intro screens
                  <button onClick={handleNext} className="main-action-button">
                    {getButtonText()}
                    {getButtonIcon()}
                  </button>
                )}
              </div>
            ) : (
              <div className="navigation-footer">
                {/* Navigation buttons with pagination in the middle */}
                <div className="navigation-controls">
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 0 && activeSection === 0}
                    className="back-button"
                  >
                    <ArrowLeft className="back-icon" />
                    {t.back}
                  </button>

                  {/* Pagination indicators in the middle */}
                  {currentSection?.steps && (
                    <div className="pagination-indicators">
                      {Array.from({ length: currentSection.steps.length }).map((_, i) => (
                        <div
                          key={i}
                          className={`pagination-dot ${i === currentStep ? "pagination-dot-active" : "pagination-dot-inactive"}`}
                        />
                      ))}
                    </div>
                  )}

                  <button
                    onClick={handleNext}
                    disabled={currentContent?.type === "quiz" && !isChecking && selectedAnswer === null}
                    className={`action-button ${
                      currentContent?.type === "quiz" && !isChecking && selectedAnswer === null
                        ? "action-button-disabled"
                        : "action-button-primary"
                    }`}
                  >
                    {getButtonText()}
                    <ArrowRight className="action-icon" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default DesignAdventureApp
